// ドライバー承認フロー（#6）API クライアント

import type { Statement } from "../types";
import { noteAuthRequired } from "./authSignal";

export type ApprovalStatus = "published" | "viewed" | "approved" | "question";

export type PublishedItem = {
  driverId: string;
  driverName: string;
  token: string;
  url: string;
  status: ApprovalStatus;
  emailSent?: boolean; // メール自動送信できたか（未設定/メール無しは false）
  emailError?: string;
};

export type MailSummary = { configured: boolean; sent: number; skipped: number; failed: number };

export type ApprovalSummary = {
  token: string;
  driverId: string;
  driverName: string;
  month: string;
  status: ApprovalStatus;
  publishedAt: string;
  viewedAt?: string | null;
  approvedAt?: string | null;
  question?: string | null;
  questionAt?: string | null;
};

// 事務所：明細を承認用に発行（emailByDriverId があればメール自動送信も行う）
export async function publishApprovals(
  month: string,
  statements: Statement[],
  emailByDriverId?: Record<string, string>
): Promise<{ ok: boolean; published?: PublishedItem[]; mail?: MailSummary; error?: string }> {
  const items = statements.map((s) => ({
    driverId: s.driverId,
    driverName: s.driverName,
    email: emailByDriverId?.[s.driverId] ?? null,
    snapshot: s,
  }));
  const r = await fetch("/api/approval/publish", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ month, items }),
  });
  noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
  return (await r.json()) as { ok: boolean; published?: PublishedItem[]; mail?: MailSummary; error?: string };
}

// 事務所：承認状況を取得
export async function fetchApprovalStatus(
  month: string
): Promise<{ ok: boolean; items: ApprovalSummary[]; error?: string }> {
  try {
    const r = await fetch(`/api/approval/status?month=${encodeURIComponent(month)}`);
    noteAuthRequired(r); // ログイン切れなら AuthGate に知らせる（2026-09-07）
    const ct = r.headers.get("content-type") || "";
    if (!ct.includes("application/json")) return { ok: false, items: [], error: "API未提供（ローカル環境）" };
    const data = (await r.json()) as { ok: boolean; items?: ApprovalSummary[]; error?: string };
    return { ok: data.ok, items: data.items || [], error: data.error };
  } catch (e) {
    return { ok: false, items: [], error: e instanceof Error ? e.message : String(e) };
  }
}

// 公開ページ：トークンで自分の明細を取得
export async function fetchApproval(
  token: string
): Promise<
  | { ok: true; driverName: string; month: string; status: ApprovalStatus; snapshot: Statement; approvedAt: string | null; question: string | null }
  | { ok: false; error: string }
> {
  try {
    const r = await fetch(`/api/approval/get?token=${encodeURIComponent(token)}`);
    const data = (await r.json()) as any;
    if (!data.ok) return { ok: false, error: data.error || "取得に失敗しました" };
    return data;
  } catch (e) {
    return { ok: false, error: e instanceof Error ? e.message : String(e) };
  }
}

// 公開ページ：承認 or 質問を送信
export async function submitApprovalAction(
  token: string,
  action: "approve" | "question",
  question?: string
): Promise<{ ok: boolean; status?: ApprovalStatus; error?: string }> {
  const r = await fetch("/api/approval/act", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, action, question }),
  });
  return (await r.json()) as { ok: boolean; status?: ApprovalStatus; error?: string };
}
